import { useQuery, UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';

import { LimitClassTypes, LimitType } from '@legacy-modules/dashboard/types/AbstractLimit';
import ValueExpression from '@legacy-modules/valueexpressions/models/valueexpressions/ValueExpression';
import { defaultLimitsMap } from '@utils/limits-utils';
import { getOrgTreeBaseUrl } from '@legacy-modules/common/services/EnvironmentConfigurationService';
import { selectTokenId } from '@redux';

type OrgConfig = { value: LimitClassTypes; orgKey: string; configKey: string };
type OrgConfigLoadResponse = {
  values: OrgConfig[];
};

type InputParameters = {
  orgKey: string;
  valueExpression: ValueExpression;
  enabled?: boolean;
};

/**
 * Custom hook that fetches the org unit's config.
 * @param parameters
 */
export default function useOrgConfig(parameters: InputParameters): UseQueryResult<OrgConfigLoadResponse> {
  const { orgKey, valueExpression, enabled = true } = parameters;
  const tokenId = useSelector(selectTokenId);
  const defaultLimitMapEntry = defaultLimitsMap.get(valueExpression);

  // Include any legacy identifiers so goals saved under a previous KPI name are still fetched.
  const configKeys = [valueExpression?.identifier, ...(valueExpression?.legacyIdentifiers ?? [])]
    .filter(Boolean)
    .join(',');

  const url = new URL(`${getOrgTreeBaseUrl()}/orgunitconfigs`);
  // There are no other configTypes so far. Once there are variants, this should be an input parameter.
  url.searchParams.append('configType', 'ValueExpressionGoal');
  url.searchParams.append('orgKeys', orgKey);
  url.searchParams.append('configKeys', configKeys);

  return useQuery<OrgConfigLoadResponse>({
    queryKey: ['ValueExpressionGoal', orgKey, valueExpression?.identifier],
    queryFn: () =>
      fetch(url, {
        headers: {
          token: tokenId,
        },
      }).then((res) => res.json()),
    enabled: enabled && !!orgKey && !!valueExpression,
    initialData: defaultLimitMapEntry && {
      values: [
        { value: { ...defaultLimitMapEntry, type: LimitType.LIMIT }, configKey: valueExpression.identifier, orgKey },
      ],
    },
  });
}
